'use client'

import { MathLib } from '@morpho-org/blue-sdk'
import { safeParseNumber } from '@morpho-org/blue-sdk-viem'
import { InterfaceChainUtils, parseApiAsset } from '@morpho-org/dapp-core'
import { bigIntComparator } from '@morpho-org/morpho-ts'
import { getMarketName, getMarketNameParts, UNKNOWN_SYMBOL } from '@morpho-org/ui-v6/utils'

import type { AggregatedApy, NetApy, NetApyMarket, RewardToken } from '@/components/common/Card'
import type { UserBorrowPosition } from '@/hooks/positions/userBorrowPositionsQuery'

import { getParsedValue } from '@/helpers/formatters'
import { rewardsCalculationFromMarket } from '@/helpers/rewards'
import { getMarketUrl } from '@/helpers/routing'
import { getShareFromTotal } from '@/helpers/vault'
import { toBorrowPositionInputToken } from '@/hooks/positions/userBorrowPositionsQuery'

/**
 * Borrow APY weighted by each position's borrowed USD. The net rate is the server's `apy.net`,
 * which already deducts every borrow reward (including rewards whose token it cannot list); the
 * listed `borrowRewards` only feed the breakdown, so no reward is deducted twice.
 */
export const calculateAggregatedData = (
  marketPositions: UserBorrowPosition[]
): AggregatedApy | null => {
  try {
    if (marketPositions.length === 0) return null

    let totalAssetsUsd = 0n
    let weightedBorrowApy = 0n
    let weightedMarketApy = 0n
    const rewardTokens: Record<string, RewardToken> = {}
    const marketApys: NetApy[] = []

    marketPositions
      .filter(
        ({ borrowAssetsUsd, collateralUsd }) =>
          (borrowAssetsUsd != null && borrowAssetsUsd > 0) ||
          (collateralUsd != null && collateralUsd > 0)
      )
      .forEach(position => {
        const borrowAssetsUsd = getParsedValue(position.borrowAssetsUsd, safeParseNumber) ?? 0n
        totalAssetsUsd += borrowAssetsUsd

        const chainId = position.market.chainId
        const loanInputToken = toBorrowPositionInputToken(position.market.loanAsset)
        const collateralInputToken = position.market.collateralAsset
          ? toBorrowPositionInputToken(position.market.collateralAsset)
          : null
        const loanAsset = parseApiAsset(loanInputToken)
        const collateralAsset = collateralInputToken ? parseApiAsset(collateralInputToken) : null
        let market: NetApyMarket | undefined
        if (collateralAsset) {
          const { loanSymbol, collateralSymbol } = getMarketNameParts({
            loanAsset,
            collateralAsset
          })
          market = {
            loanSymbol,
            loanLogoURI: loanAsset.logoURI,
            collateralSymbol: collateralSymbol ?? UNKNOWN_SYMBOL,
            collateralLogoURI: collateralAsset.logoURI
          }
        }

        const nativeRate = getParsedValue(position.market.apy.native, safeParseNumber)
        const netRate = getParsedValue(position.market.apy.net, safeParseNumber)

        const rewards = rewardsCalculationFromMarket({
          market: {
            state: {
              rewards: position.market.borrowRewards.map(({ asset, borrowApr }) => ({
                asset: toBorrowPositionInputToken(asset),
                borrowApr
              }))
            },
            loanAsset: loanInputToken
          },
          type: 'borrow'
        })

        if (netRate) {
          weightedBorrowApy += MathLib.wMulDown(netRate, borrowAssetsUsd)
        }

        if (nativeRate) {
          weightedMarketApy += MathLib.wMulDown(nativeRate, borrowAssetsUsd)
        }

        rewards.forEach(reward => {
          if (!(reward.rewardToken.address in rewardTokens)) {
            rewardTokens[reward.rewardToken.address] = {
              totalApr: 0n,
              asset: {
                symbol: reward.rewardToken.symbol ?? '',
                logoURI: reward.rewardToken.logoURI ?? ''
              },
              items: []
            }
          }

          // oxlint-disable-next-line typescript/consistent-type-assertions
          const rewardToken = rewardTokens[reward.rewardToken.address] as RewardToken

          rewardToken.totalApr += MathLib.wMulDown(reward.apr ?? 0n, borrowAssetsUsd)

          rewardToken.items.push({
            name: getMarketName({
              loanAsset,
              collateralAsset,
              maxLength: 15
            }),
            apr: reward.apr ?? 0n,
            weight: borrowAssetsUsd,
            chainId,
            logoURIs: [loanAsset.logoURI, collateralAsset?.logoURI],
            market,
            allocation: 0n,
            percentageOffset: 0n
          })
        })

        const identifier = InterfaceChainUtils.getIdentifierById(chainId)

        marketApys.push({
          name: getMarketName({
            loanAsset,
            collateralAsset,
            maxLength: 15
          }),
          apy: netRate ?? 0n,
          weight: borrowAssetsUsd,
          allocation: 0n,
          percentageOffset: 0n,
          chainId,
          logoURIs: [loanAsset.logoURI, collateralAsset?.logoURI],
          market,
          href: getMarketUrl({
            identifier,
            marketId: position.market.marketId,
            collateralSymbol: collateralAsset ? collateralAsset.symbol || UNKNOWN_SYMBOL : '',
            loanSymbol: loanAsset.symbol || UNKNOWN_SYMBOL
          }),
          hoverCardProps: {
            nativeApy: nativeRate,
            netApy: netRate,
            rewards: rewards.map(r => ({
              ...r,
              apr: -(r.apr ?? 0n)
            }))
          }
        })
      })

    const avgBorrowApy =
      totalAssetsUsd > 0n ? MathLib.wDivDown(weightedBorrowApy, totalAssetsUsd) : 0n

    const avgMarketApy =
      totalAssetsUsd > 0n ? MathLib.wDivDown(weightedMarketApy, totalAssetsUsd) : 0n

    const normalizedRewardTokens: RewardToken[] = Object.values(rewardTokens).map(rewardToken => {
      const totalApr =
        totalAssetsUsd > 0n ? MathLib.wDivDown(rewardToken.totalApr, totalAssetsUsd) : 0n

      const itemTotalWeightedApr = rewardToken.items.reduce(
        (sum, i) => sum + MathLib.wMulDown(i.weight, i.apr),
        0n
      )

      let rewardSum = 0n

      return {
        ...rewardToken,
        totalApr,
        items: rewardToken.items
          .toSorted(bigIntComparator(({ weight }) => weight, 'desc'))
          .map(item => {
            const newItem = {
              ...item,
              allocation: getShareFromTotal(
                itemTotalWeightedApr,
                MathLib.wMulDown(item.weight, item.apr)
              ),
              percentageOffset: rewardSum
            }

            rewardSum += newItem.allocation

            return newItem
          })
      }
    })

    let marketSum = 0n

    const normalizedMarketApys = marketApys
      .toSorted(bigIntComparator(({ weight }) => weight, 'desc'))
      .map(item => {
        const newItem = {
          ...item,
          allocation: getShareFromTotal(totalAssetsUsd, item.weight),
          percentageOffset: marketSum
        }

        marketSum += newItem.allocation

        return newItem
      })

    return {
      apy: avgBorrowApy,
      itemApy: avgMarketApy,
      rewardTokens: normalizedRewardTokens,
      itemApys: normalizedMarketApys
    }
  } catch (error) {
    throw new Error('Error calculating aggregated data for useApyAggregatedData', {
      cause: error
    })
  }
}
